<template>
    <div class="w-full">
        <div class="mt-1 flex justify-between lg:flex-row flex-col">
            <div class="w-auto">
                <a-input-search
                    data-cy="claimant-input-search"
                    @search="onSearch"
                    v-model:value="search_value"
                    :placeholder="$t('globals.tables.search')"
                />
            </div>
            <div class="pt-4 lg:pt-0">
                <a-button
                    data-cy="advance-search-show-button"
                    type="primary"
                    @click="onShowAdvanceSearch"
                    v-if="!is_advance_search_visible"
                >
                    {{ $t('claims.claimants.table.actions.show_advance_search') }}
                </a-button>
                <a-button
                    data-cy="advance-search-hide-button"
                    type="primary"
                    @click="onHideAdvanceSearch"
                    v-if="is_advance_search_visible"
                >
                    {{ $t('claims.claimants.table.actions.hide_advance_search') }}
                </a-button>
            </div>
        </div>
        <div class="w-full" v-if="is_advance_search_visible">
            <form-claimant-admin-search
                :search_form="search_form"
                :section_type="section_type"
                :processing="processing"
                :validation="validation"
                @clear="handleFormClear"
                @formSubmit="handleFormSubmit"
            />
        </div>
        <div class="pt-2 md:pt-4">
            <div class="w-full">
                <a-table
                    data-cy="claimants-list"
                    :columns="getColumns"
                    :dataSource="data"
                    :rowKey="(record) => record.id"
                    :pagination="pagination"
                    :locale="locale"
                    :loading="loading"
                    @change="handleTableChange"
                    size="middle"
                    class="ant-table-striped"
                >
                    <template #bodyCell="{ column, record, text }">
                        <template v-if="column.key === 'actions'">
                            <a-button-group>
                                <a-tooltip placement="top" :title="$t('claims.claimants.table.actions.view')">
                                    <a-button
                                        data-cy="edit-button"
                                        class="text-blue-400 hover:text-blue-400 focus:text-blue-400"
                                        @click="onTableAction(record.id, 'view')"
                                    >
                                        <FolderOpenOutlined />
                                    </a-button>
                                </a-tooltip>
                            </a-button-group>
                        </template>
                        <template v-else-if="column.key === 'date_of_loss'">
                            <span class="whitespace-nowrap">
                                {{ text }}
                            </span>
                        </template>
                        <template v-else>
                            {{ text }}
                        </template>
                    </template>
                </a-table>
                <span v-if="data.length > 0" data-cy="page-item-range" class="page-item-range flex">
                    {{
                        $t('claims.claimants.table.page_range', {
                            pi_start: pageItemStart,
                            pi_end: pageItemEnd,
                            total: pagination.total,
                        })
                    }}
                </span>
                <span
                    v-if="
                        (section_type == 'site_admin' || section_type == 'association' || section_type == 'company') &&
                        data.length > 0
                    "
                    data-cy="search-items-found"
                    class="font-semibold block pb-1 md:inline-block md:pb-0 md:pr-4"
                >
                    {{
                        $t('claims.claimants.search_form.search_results_split', {
                            motor_total: search_motor_count,
                            non_motor_total: search_non_motor_count,
                        })
                    }}
                </span>
            </div>
        </div>
    </div>
</template>

<script>
import FormClaimantAdminSearch from '@/components/forms/claimants/AdminSearch.vue';
import { FolderOpenOutlined } from '@ant-design/icons-vue';

export default {
    components: {
        FormClaimantAdminSearch,
        FolderOpenOutlined,
    },
    props: {
        section_type: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            data: [],
            search_value: null,
            is_advance_search_visible: false,
            pagination: {
                pageSize: 10,
                showSizeChanger: false,
                total: 0,
            },
            locale: {
                emptyText: this.$t('claims.claimants.table.no_data'),
            },
            loading: false,
            current_filter: {},
            sorter: {},
            params: {},
            search_motor_count: 0,
            search_non_motor_count: 0,
            processing: false,
            search_form: {
                association_id: null,
                company_id: null,
                claim_no: null,
                name: '',
                national_id: '',
                driver_lic_no: '',
                claim_year: null,
                reg_no: '',
                chassis_no: '',
                engine_no: '',
                dob: null,
                loss_date_range: {
                    start_date: null,
                    end_date: null,
                },
                date_imported_range: {
                    start_date: null,
                    end_date: null,
                },
                file_source: '',
            },
            validation: {
                errors: {},
                message: '',
            },
        };
    },
    mounted() {
        this.fetch();
    },
    computed: {
        getColumns() {
            return [
                {
                    key: 'actions',
                    width: 35,
                },
                {
                    title: this.$t('claims.claimants.table.headers.claim_no'),
                    key: 'claim_id',
                    dataIndex: 'claim_id',
                    sorter: true,
                },
                {
                    title: this.$t('claims.claimants.table.headers.insurer'),
                    key: 'company_code',
                    dataIndex: 'company_code',
                    sorter: true,
                },
                {
                    title: this.$t('claims.claimants.table.headers.loss_data'),
                    key: 'date_of_loss',
                    dataIndex: 'date_of_loss',
                    sorter: true,
                },
                {
                    title: this.$t('claims.claimants.table.headers.claimant'),
                    key: 'claimant_name',
                    dataIndex: 'claimant_name',
                    sorter: true,
                },
                {
                    title: this.$t('claims.claimants.table.headers.class'),
                    key: 'class_of_business',
                    dataIndex: 'class_of_business',
                    sorter: true,
                },
                {
                    title: this.$t('claims.claimants.table.headers.description'),
                    key: 'risk_description',
                    dataIndex: 'risk_description',
                    sorter: true,
                },
            ];
        },
        pageItemStart() {
            let current_page = this.pagination.current;

            if (current_page === undefined) current_page = 1;

            return (current_page - 1) * this.pagination.pageSize + 1;
        },
        pageItemEnd() {
            let current_page = this.pagination.current,
                page_item_end = 0;

            if (current_page === undefined) current_page = 1;

            page_item_end = current_page * this.pagination.pageSize;

            if (this.pagination.total !== undefined && this.pagination.total < page_item_end) {
                return this.pagination.total;
            } else {
                return page_item_end;
            }
        },
    },
    methods: {
        handleTableChange(pagination, filters, sorter) {
            const pager = { ...this.pagination };

            pager.current = pagination.current;

            this.pagination = pager;
            this.current_filter = filters;
            this.sorter = sorter;

            this.fetch({
                ...this.params,
                results: pagination.pageSize,
                page: pagination.current,
                sortField: sorter.field,
                sortOrder: sorter.order,
                ...filters,
            });
        },
        fetch(params = {}, search_id = '') {
            if (search_id !== '') {
                this.search_id = search_id;
            }

            this.loading = true;
            this.params = params;

            this.fetchData();
            this.fetchDelayedData();
        },
        fetchData() {
            let params = this.params;
            let urls = [];

            switch (this.section_type) {
                case 'site_admin':
                    urls = [
                        this.$http.get('/api/admin/claimants/search', {
                            params: params,
                        }),
                        this.$http.get('/api/admin/claimants/search-count', {
                            params: params,
                        }),
                    ];
                    break;
                case 'association':
                    urls = [
                        this.$http.get('/api/associations/claimants/search', {
                            params: params,
                        }),
                        this.$http.get('/api/associations/claimants/search-count', {
                            params: params,
                        }),
                    ];
                    break;
                case 'company':
                    params.company_id = this.$store.getters['user/currentCompanyId'];
                    urls = [
                        this.$http.get('/api/companies/claimants/search', {
                            params: params,
                        }),
                        this.$http.get('/api/companies/claimants/search-count', {
                            params: params,
                        }),
                    ];
                    break;
            }

            if (urls.length > 0) {
                this.$http
                    .all(urls)
                    .then(
                        this.$http.spread((response, count) => {
                            const pagination = { ...this.pagination };

                            // Read total count from server
                            pagination.total = count.data.meta.total_count;

                            this.data = response.data.data;
                            this.pagination = pagination;
                        }),
                    )
                    .catch((error) => {
                        if (error.response.status == 403) {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.403_mod'),
                            });
                        } else {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.any', { code: 'CVCLAF01' }),
                            });
                        }
                    })
                    .finally(() => {
                        this.loading = false;
                    });
            }
        },
        fetchDelayedData() {
            let params = this.params;
            let url = '';

            switch (this.section_type) {
                case 'site_admin':
                    url = '/api/admin/claimants/search-split-count';
                    break;
                case 'association':
                    url = '/api/associations/claimants/search-split-count';
                    break;
                case 'company':
                    url = '/api/companies/claimants/search-split-count';
            }

            if (url !== '') {
                this.$http
                    .get(url, {
                        params: params,
                    })
                    .then((response) => {
                        this.$emit('fetchSplitCount', {
                            search_motor_count: response.data.meta.total_motor,
                            search_non_motor_count: response.data.meta.total_non_motor,
                        });

                        this.search_motor_count = response.data.meta.total_motor;
                        this.search_non_motor_count = response.data.meta.total_non_motor;
                    })
                    .catch((error) => {
                        if (error.response.status == 403) {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.403_mod'),
                            });
                        } else {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.any', { code: 'CVCLAF02' }),
                            });
                        }
                    })
                    .finally(() => {
                        this.loading = false;
                    });
            }
        },
        onSearch(input) {
            let regex = /^[a-zA-Z0-9'\s\(\)_-]+$/;

            if (regex.test(input) || input === '') {
                this.fetch({
                    basic_search: this.search_value,
                });
                this.pagination.current = 1;
            } else {
                this.search_value = null;

                this.$notification.error({
                    message: this.$t('globals.tables.invalid_characters'),
                });
            }
        },
        onShowAdvanceSearch() {
            this.handleFormClear();
            this.is_advance_search_visible = true;
        },
        onHideAdvanceSearch() {
            this.is_advance_search_visible = false;
        },
        onTableAction(id, key) {
            this.$emit('onTableAction', {
                id: id,
                key: key,
            });
        },
        handleFormClear() {
            this.search_form = {
                association_id: null,
                company_id: null,
                claim_no: null,
                name: '',
                national_id: '',
                driver_lic_no: '',
                claim_year: null,
                reg_no: '',
                chassis_no: '',
                engine_no: '',
                dob: null,
                loss_date_range: {
                    start_date: null,
                    end_date: null,
                },
                date_imported_range: {
                    start_date: null,
                    end_date: null,
                },
                file_source: '',
            };
        },
        handleFormSubmit() {
            let payload = {};
            this.search_value = null;

            _.forEach(this.search_form, function (value, key) {
                if (typeof value === 'object' && value !== null) {
                    if (this.$dayjs.isDayjs(value)) {
                        payload[key] = value.format('YYYY-MM-DD');
                    } else if (
                        (value.start_date !== undefined && value.start_date !== null) ||
                        (value.end_date !== undefined && value.end_date !== null)
                    ) {
                        payload[key] = {
                            start_date:
                                value.start_date !== undefined && value.start_date !== null
                                    ? value.start_date.format('YYYY-MM-DD')
                                    : null,
                            end_date:
                                value.end_date !== undefined && value.end_date !== null
                                    ? value.end_date.format('YYYY-MM-DD')
                                    : null,
                        };
                    }
                } else if (value !== null && value !== '') {
                    payload[key] = value;
                }
            });

            this.fetch(payload);
        },
        reload() {
            const filters = this.current_filter;

            this.fetch({
                ...this.params,
                results: this.pagination.pageSize,
                page: this.pagination.current,
                sortField: this.sorter.field,
                sortOrder: this.sorter.order,
                ...filters,
            });
        },
    },
};
</script>
